import { useGetInvoiceTransaction } from '@/context/services/reportsType/invoiceReports/Edit.service';
import { isSuccess } from '@/context/services/type';
import { useQueries } from '@tanstack/react-query';
import { getGetApPayeesInfoQueryKey, useGetApPayeesInfoHook } from 'ap-openapi';
import { useMemo } from 'react';

export type PayeeContactOfficeMember = {
  id: string;
  name: string;
  number: string;
};

/**
 * Backs the "add payee's in-house contacts" one-click action: fetches
 * contact office members for every payee (ap_payee) referenced by the
 * report's line items, and dedupes them. Reuses the query cache already
 * populated by the TransactionsDetail section (`useGetInvoiceTransaction`
 * is the same query, keyed by `reportId`).
 */
export const useInvoiceReportPayeeContacts = (
  reportId: string
): PayeeContactOfficeMember[] => {
  const transactions = useGetInvoiceTransaction(reportId);

  const payeeIds = useMemo(
    () =>
      Array.from(
        new Set(
          transactions
            .map((transaction) => transaction.ap_payee?.id)
            .filter((id): id is string => !!id)
        )
      ),
    [transactions]
  );

  const getApPayeesInfo = useGetApPayeesInfoHook();
  const results = useQueries({
    queries: payeeIds.map((id) => ({
      queryKey: getGetApPayeesInfoQueryKey(id),
      queryFn: () => getApPayeesInfo(id),
    })),
  });

  return useMemo(() => {
    const contacts = new Map<string, PayeeContactOfficeMember>();
    for (const result of results) {
      const resp = result.data;
      if (!resp || !isSuccess(resp.status)) continue;
      for (const contact of resp.data?.contact_office_members ?? []) {
        if (!contact.id) continue;
        contacts.set(contact.id, {
          id: contact.id,
          name: contact.display_name,
          number: contact.number,
        });
      }
    }
    return Array.from(contacts.values());
  }, [results]);
};
